import { chunkRecovery } from "@/lib/chunkRecovery";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";

// "Neue Version verfügbar": sw.ts runs with skipWaiting + clientsClaim, so a new service
// worker takes over every open tab as soon as it is installed (controllerchange). The
// tab's JavaScript is still the old build until it reloads, and its not-yet-loaded lazy
// chunks are gone from the new precache. The tab is TOLD (a toast with "Neu laden"); it is
// never reloaded automatically, because that would throw away typed text.
//
// Two takeovers are not news and stay quiet (both measured in a real browser):
//   * the very first install: the page was not controlled at load (and no worker was
//     active), the claim only attaches the worker to the page that already runs this
//     build. A hard-reloaded page (Shift+Reload) also starts without a controller, but a
//     worker IS active then, so its next takeover is a real update and is announced;
//   * a page that loaded the NEW shell through the old worker (fast network after a
//     deploy) and is claimed by the new worker a second later. The check: is this page's
//     entry script in the active precache once the new worker is activated (the purge of
//     the old precache runs during activation)?

const NEW_VERSION_TOAST_MS = 10 * 60_000;

export interface ServiceWorkerContainerLike {
  controller: unknown;
  addEventListener(type: "controllerchange", listener: () => void): void;
  removeEventListener(type: "controllerchange", listener: () => void): void;
}

export interface NewVersionWatchDeps {
  sw: ServiceWorkerContainerLike;
  /** Only asked when the page was not controlled at load: was a worker active anyway?
   * True after a hard reload (Shift+Reload bypasses the worker, so the page starts
   * without a controller); false on the very first install. */
  activeWorkerAtLoad?: () => Promise<boolean>;
  /** true = page runs the active build, false = outdated, null = cannot tell. */
  isOwnBuildCurrent: () => Promise<boolean | null>;
  isRecovering: () => boolean;
  notify: () => void;
}

export function watchForNewVersion(deps: NewVersionWatchDeps): () => void {
  let controlledAtStart = !!deps.sw.controller;
  if (!controlledAtStart && deps.activeWorkerAtLoad) {
    // Resolves long before a worker could install and claim the page; if a takeover came
    // first anyway, it counts as a first install (quiet), as before.
    void deps.activeWorkerAtLoad().then(
      (active) => {
        if (active) controlledAtStart = true;
      },
      () => undefined,
    );
  }
  let announced = false;
  const onChange = () => {
    if (!controlledAtStart) {
      controlledAtStart = true;
      return;
    }
    if (announced || deps.isRecovering()) return;
    void deps.isOwnBuildCurrent().then((current) => {
      if (current === true || announced || deps.isRecovering()) return;
      announced = true;
      deps.notify();
    });
  };
  deps.sw.addEventListener("controllerchange", onChange);
  return () => deps.sw.removeEventListener("controllerchange", onChange);
}

/** Is the page's own entry script in the active service worker's precache? */
export async function ownBuildIsCurrent(
  doc: Document,
  cacheStorage: CacheStorage | undefined,
): Promise<boolean | null> {
  const entry = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  if (!entry || !cacheStorage) return null;
  try {
    const names = await cacheStorage.keys();
    const precache = names.find((n) => n.includes("precache"));
    if (!precache) return null;
    const cache = await cacheStorage.open(precache);
    return (await cache.match(entry.src)) !== undefined;
  } catch {
    return null;
  }
}

function waitUntilActivated(worker: ServiceWorker | null, timeoutMs = 5_000): Promise<void> {
  if (!worker || worker.state === "activated" || worker.state === "redundant") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      worker.removeEventListener("statechange", onState);
      resolve();
    };
    const onState = () => {
      if (worker.state === "activated" || worker.state === "redundant") done();
    };
    const timer = setTimeout(done, timeoutMs);
    worker.addEventListener("statechange", onState);
  });
}

export function showNewVersionToast(): void {
  showToast(t.newVersionAvailable, "info", NEW_VERSION_TOAST_MS, {
    label: t.reloadPage,
    onClick: () => chunkRecovery.reloadForUpdate(),
  });
}

/** Wire the prompt in the browser. Call at startup, BEFORE the service worker registers,
 * so "was this page controlled at load" is read correctly. */
export function installNewVersionPrompt(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;
  watchForNewVersion({
    sw,
    activeWorkerAtLoad: () => sw.getRegistration().then((reg) => !!reg?.active),
    isOwnBuildCurrent: async () => {
      await waitUntilActivated(sw.controller);
      return ownBuildIsCurrent(document, typeof caches === "undefined" ? undefined : caches);
    },
    isRecovering: () => chunkRecovery.getPhase() !== "idle",
    notify: showNewVersionToast,
  });
}
